import { filterPmoreEntries, renderPmoreMarkup } from "./pmore.js";
import { pathForApp } from "../runtime/stage-router.js";
import { OWNER_STAGE_LABELS, ownerStagePath, PMENU_PRIMARY_STAGE_NAMES, } from "../stages/owner-stage-routes.js";
import nodeMarkUrl from "../skin/brand/node-mark.jpg?url";
import pmenuMarkUrl from "../skin/brand/pmenu-mark.png?url";
import { escapeHtml } from "../util/escape-html.js";
import { currentDeviceLabel } from "./current-device.js";
import { currentMediaPreferences } from "./media-preferences.js";
const DEFAULT_DEVICE_LABEL = currentDeviceLabel(undefined);
const PROFILE_PATH = pathForApp("profile");
/** Recents cap — the source's own `recentApps = [key, ...].slice(0, 3)`
 * (`core/shell/menu/index.js` ~721-767). */
const RECENTS_LIMIT = 3;
/** Hover-open close delay, in ms — verbatim from the source's own `HOVER_CLOSE_DELAY`
 * (`core/shell/menu/index.js` ~537-554). */
const HOVER_CLOSE_DELAY = 140;
/**
 * Per-item icon markup, copied VERBATIM from the original skin's `#pmenu` primary rows
 * (`SKIN/index.html` lines 71/76/80 — the `<svg viewBox="0 0 20 20" ...>` inner paths for
 * Home/Agents/Friends respectively). `viewBox`/`fill`/`stroke`/`stroke-width` are the same
 * on every `.pmenu-ic svg` (menu.css scopes them there, not per-icon), so only the inner
 * path/circle markup varies here.
 */
const PMENU_PRIMARY_ICONS = {
    agents: '<path d="M10 2 L12 8 L18 10 L12 12 L10 18 L8 12 L2 10 L8 8 Z"/>',
    friends: '<circle cx="7" cy="7" r="2.6"/><circle cx="14" cy="8.2" r="2.1"/><path d="M3 17 V14.5 Q5 12.5 7 12.5 Q9 12.5 11 14.5 V17"/><path d="M12 17 V15 Q13 13.6 14 13.6 Q15.5 13.6 16.8 15 V17"/>',
    home: '<path d="M3 10.5 L10 3.5 L17 10.5"/><path d="M5 9 V17 H15 V9"/>',
};
/**
 * The pmenu's primary dock list — epic #1507 fidelity fix. Exactly the original skin's
 * three `.pmenu-item` primary rows (`SKIN/index.html` lines 69-89: Home, Agents, Friends,
 * then `.pmenu-more-btn`) — see `PMENU_PRIMARY_STAGE_NAMES`'s own doc comment
 * (`owner-stage-routes.ts`) for why Wallet/Profile are deliberately NOT here.
 */
const PMENU_PRIMARY_ITEMS = PMENU_PRIMARY_STAGE_NAMES.map((name) => ({
    icon: PMENU_PRIMARY_ICONS[name],
    label: OWNER_STAGE_LABELS[name],
    name,
    path: ownerStagePath(name),
}));
/**
 * Port of the original skin's `.pmenu` OS-level app dock (`SKIN/index.html` `#pmenu` +
 * `SKIN/core/shell/menu/index.js`) — the primary desktop nav (Mode 1: the fixed left
 * dock, `.topbar` hidden ≥769px). Ports the structure and the collapse interaction, plus
 * the `.pmenu-more-btn` → `.pmore` mega-panel flyout: colored-group columns + recents +
 * search (`pmore.ts`), hover-open with a close delay on fine pointers IN ADDITION to the
 * click-toggle (epic #1507 fidelity F5, below) — the easter eggs in the source `index.js`
 * stay out of scope.
 *
 * epic #1507 fidelity fix: the PRIMARY list is exactly the original's three `.pmenu-item`
 * rows (`PMENU_PRIMARY_ITEMS`, above — Home/Agents/Friends), always present in fixed order,
 * followed by the `.pmenu-more-btn` "Stages + more" trigger — matching `SKIN/index.html`
 * lines 69-89 verbatim. Registry-backed stage apps (the same StageRegistryStore-backed
 * `NavItem[]` app-shell.ts computes for `#items`) are reachable ONLY through the `.pmore`
 * flyout (`pmore.ts`) — the original has no secondary inline list of them inside `.pmenu`
 * itself, so this component no longer renders one either (a previous, non-faithful revision
 * did; it has been removed).
 *
 * `.pmenu` is the shell's SOLE stage navigation under Mode 1 — `.rail` no longer renders
 * a duplicate `nav.ts` list (see app-shell.ts). Home/End/Arrow roving keyboard navigation
 * spans the primary `.pmenu-item` entries.
 *
 * The shell-reserved "Settings" entry (previously rendered as a fourth primary-list row) has
 * been removed to match the original, which has no such row — Settings will get its own
 * reachable surface in a later task, not a `.pmenu-item`.
 *
 * `.pmenu-bottom` carries only the `.pmenu-id` identity chip (`userChip`), matching the
 * original (`SKIN/index.html` lines 92-97) — the connectivity badge / sync indicator / sync
 * center (`<node-connectivity-badge>` / `<node-sync-indicator>` / `<node-sync-center>`) used
 * to be mounted here too, but the original never puts ambient sync/connectivity status
 * inside `.pmenu` at all (its own `.pmenu-bottom` is just `.pmenu-id`). They remain mounted
 * in `.topbar` for the <769px mobile fallback (topbar.ts) — TODO: give them a home in a
 * desktop Settings view once one exists, rather than re-adding them to `.pmenu`.
 *
 * `render()` is destructive (`this.innerHTML = ...`) and runs on every prop setter
 * (`items`/`currentPath`/`state`/`client`) — including background ticks unrelated to the
 * `.pmore` flyout itself (app-shell's `transport.state.v1` liveQuery fires all three on
 * ordinary connectivity/sync ticks). While the flyout is open, `render()` snapshots and
 * restores `.pmore-search`'s live value/selection/focus around the rebuild so a background
 * tick can't silently drop a half-typed query or steal focus back to an emptied input (see
 * `#captureSearchSnapshot`/`#restoreSearchSnapshot`). Opening/closing the flyout itself
 * goes through `#openPmore`/`#closePmore`, which capture/restore focus around the toggle
 * the same way `node-dialog.ts` does for its own `#previousFocus` — but because `render()`
 * always recreates the whole subtree (including `.pmenu-more-btn` itself), a raw DOM-node
 * reference captured before a render doesn't survive it; the trigger is re-resolved by
 * selector after the close-render instead (see `#closePmore`). The one exception is the
 * entry-selection close, which also navigates: app-shell.ts's own post-navigation
 * `#focusOutlet()` already moves focus into the newly-mounted stage once it's ready, so
 * `#closePmore` is called there with `restoreFocus: false` rather than fighting that (already
 * correct, already the convention for every other navigation) with a focus target that would
 * just get silently overridden a moment later.
 *
 * Escape-dismiss is a `document`-level `keydown` listener registered only while the flyout
 * is open (`#handleDocumentKeyDown`, added in `#openPmore` / removed in `#closePmore`) —
 * the SAME registration pattern as the click-outside listener, and a verbatim port of the
 * source's own `document.addEventListener('keydown', ...)` Escape close
 * (`core/shell/menu/index.js` ~567-569). It used to be handled only inside this component's
 * own bubbling `keydown` listener, which silently made Escape a no-op whenever focus sat
 * OUTSIDE `<node-shell-pmenu>` (the common case: app-shell's `#focusOutlet()` moves focus
 * into the stage outlet after every navigation) — issue #1669's "Escape doesn't close the
 * flyout".
 *
 * epic #1507 fidelity F5: two additions to the flyout, both ported from the source
 * (`core/shell/menu/index.js`):
 *  1. Hover-open — on fine pointers (`matchMedia('(hover: hover) and (pointer: fine)')`,
 *     resolved once at connect time, matching the source's own one-shot `fine` check),
 *     moving the pointer over `.pmenu-more-btn` opens the flyout without a click, and
 *     leaving BOTH the trigger and the panel schedules a close after `HOVER_CLOSE_DELAY`ms
 *     (cancelled if the pointer re-enters either before it fires) — this is IN ADDITION to
 *     the existing click-toggle, not a replacement (`#handleMouseOver`/`#handleMouseOut`,
 *     wired via delegated `mouseover`/`mouseout` on `this` rather than non-bubbling
 *     `mouseenter`/`mouseleave`, since `render()`'s destructive rebuild would otherwise
 *     require re-attaching direct listeners on every render). Because `render()` replaces
 *     the node under a stationary cursor, browsers re-dispatch SYNTHESIZED boundary events
 *     against the rebuilt DOM without any real pointer movement (Gecko with
 *     `relatedTarget: null`, Chromium usually with the detached old node) — those refires
 *     must not drive hover state, or an Escape-close re-opens itself on the very next
 *     refire (issue #1669; the pre-existing Firefox `aria-hidden` failures in
 *     pmenu-more.spec.ts/shell.spec.ts). `#isSynthesizedHoverRefire` filters them by
 *     coordinates alone — a boundary event at the exact position of the last `mousemove`
 *     cannot be genuine pointer movement, because both engines dispatch the boundary
 *     events of a real hit-target change BEFORE the `mousemove` carrying that same new
 *     position (see `#handleMouseMove`'s doc; `relatedTarget` is NOT a usable signal —
 *     Gecko's post-close refire hands over a live attached node). The original needs no
 *     such filter only because it never rebuilds these nodes. Hover-open also
 *     deliberately does NOT autofocus `.pmore-search` (the source's hover path moves no
 *     focus either — `setFly` is class-toggling only); only the click-toggle open does, so
 *     a pointer merely crossing the trigger can never yank keyboard focus away from where
 *     the user was working (`#closePmore`'s hover-timer call site likewise skips the focus
 *     restore unless focus actually lives inside the trigger+panel region).
 *  2. Recents — `#recents` is a session-local (in-memory only, resets on reload — matching
 *     the source's own in-memory `recentApps` array) list of up to `RECENTS_LIMIT`
 *     most-recently-opened registry-stage paths, pushed to whenever a click resolves to a
 *     path present in `#items` (`#pushRecent`, mirroring the source's own
 *     `pushRecent`/`RECENT_VIEWS` gate — primary Home/Agents/Friends rows and the profile
 *     chip are never in `#items`, so they're naturally excluded, matching the source's own
 *     exclusion of its always-visible dock items from recents-worthiness).
 */
export class NodeShellPmenuElement extends HTMLElement {
    #collapsed = false;
    #currentPath = "/settings";
    /** Real current-device label (`app-shell.ts` resolves it via `current-device.ts` once the
     * kernel client is live) — defaults to the same generic fallback `currentDeviceLabel`
     * itself returns for "no device known yet" so the chip never shows the old hard-coded
     * `"Node"` placeholder, not even for a first paint before that resolution completes. */
    #deviceLabel = DEFAULT_DEVICE_LABEL;
    /** Whether the current pointer environment supports real hovering — resolved once at
     * connect time (matching the source's own one-shot `fine` check), not re-evaluated on
     * every render. `false` in any environment without media-query support (the port's
     * own documented contract, and what `NOOP_MEDIA_PREFERENCES` returns before the
     * composition root registers the real adapter) so hover-open never activates there
     * instead of throwing. */
    #finePointer = false;
    #hoverCloseTimer;
    /** Real unread/attention count for the Home row's `.pmenu-badge` — `undefined` (never a
     * fabricated number) until a real source is wired; see the class doc + task brief. */
    #homeBadgeCount;
    #items = [];
    /** Viewport coordinates of the last `mousemove` observed over this component — the
     * reference point `#isSynthesizedHoverRefire` compares boundary events against (see its
     * own doc + the class doc's "Hover-open" section). `undefined` until the first move
     * arrives, so a first-ever boundary event is always treated as genuine. */
    #lastPointerX;
    #lastPointerY;
    #pmoreOpen = false;
    /** Set when `#handleMouseOver` opens the flyout via hover (fine-pointer only). A real
     * mouse click always dispatches `mouseover` immediately before `click` for the SAME
     * gesture the very first time the pointer lands on the trigger (there is no gap for a
     * user to "decide" between the two) — without this guard, `#handleClick`'s own
     * open/close TOGGLE would immediately re-close a panel the preceding `mouseover` on
     * this identical gesture just opened, so the trigger's first real click silently did
     * nothing (confirmed live: Playwright's `locator.click()` reproduces the exact same
     * mousemove -> mouseover -> mousedown -> mouseup -> click sequence a real mouse does).
     * Consumed (reset to `false`) by the very next `#handleClick` on the trigger while the
     * panel is still open — a genuinely separate, later click (mouse already resting on the
     * trigger, no new `mouseover` fires) finds it already `false` and toggles normally.
     * Also cleared by `#closePmore` itself: after a hover-open followed by a hover-away
     * close, a stale `true` here used to make the NEXT click on the trigger silently
     * consume the flag instead of re-opening (issue #1669). */
    #justOpenedByHover = false;
    /** Whether the PREVIOUS `render()` call already had the flyout open — distinguishes a
     * background re-render of an already-open panel (snapshot + restore `.pmore-search`)
     * from the transition that just opened it (no snapshot to restore). */
    #pmoreWasOpen = false;
    #previousFocus = null;
    /** Session-local recents — see the class doc's "Recents" section. Most-recent-first,
     * capped at `RECENTS_LIMIT`, deduplicated by path. */
    #recents = [];
    connectedCallback() {
        this.addEventListener("click", this.#handleClick);
        this.addEventListener("keydown", this.#handleKeyDown);
        this.addEventListener("input", this.#handleInput);
        this.addEventListener("mouseover", this.#handleMouseOver);
        this.addEventListener("mouseout", this.#handleMouseOut);
        this.addEventListener("mousemove", this.#handleMouseMove);
        this.#finePointer = currentMediaPreferences().hasFinePointer();
        this.render();
    }
    disconnectedCallback() {
        this.removeEventListener("click", this.#handleClick);
        this.removeEventListener("keydown", this.#handleKeyDown);
        this.removeEventListener("input", this.#handleInput);
        this.removeEventListener("mouseover", this.#handleMouseOver);
        this.removeEventListener("mouseout", this.#handleMouseOut);
        this.removeEventListener("mousemove", this.#handleMouseMove);
        this.#cancelHoverClose();
        document.removeEventListener("click", this.#handleOutsideClick);
        document.removeEventListener("keydown", this.#handleDocumentKeyDown);
    }
    set currentPath(value) {
        this.#currentPath = value;
        this.render();
    }
    /** Real device label from `current-device.ts` — see the class field's own doc for the
     * fallback contract. */
    set currentDeviceName(value) {
        this.#deviceLabel = value;
        this.render();
    }
    /** Real Home-row `.pmenu-badge` count — never set to a fabricated value; leave
     * `undefined` (the default) until a real unread/attention source exists. */
    set homeBadgeCount(value) {
        this.#homeBadgeCount = value;
        this.render();
    }
    set items(value) {
        this.#items = value;
        this.render();
    }
    render() {
        const deviceLabel = this.#deviceLabel;
        const initials = deviceInitials(deviceLabel);
        const currentPath = this.#currentPath;
        // See the class doc: only snapshot when this render is a re-render of an ALREADY-open
        // panel. The transition that opens it (`#pmoreWasOpen` still false) has nothing to
        // restore — `#openPmore` owns the (click-open-only) fresh autofocus instead.
        const searchSnapshot = this.#pmoreOpen && this.#pmoreWasOpen
            ? this.#captureSearchSnapshot()
            : null;
        this.innerHTML = `
      <nav class="pmenu" aria-label="Primary navigation" data-collapsed="${this.#collapsed ? "true" : "false"}">
        <div class="pmenu-head">
          <img class="pmenu-logo" src="${nodeMarkUrl}" alt="Node" title="Economy V1 network">
          <img class="pmenu-mark-img pm-min" src="${pmenuMarkUrl}" alt="N">
          <button class="pmenu-collapse" type="button" data-pmenu-collapse aria-label="Collapse menu" title="Collapse menu">
            <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M8.5 3.5 L5 7 L8.5 10.5"/></svg>
          </button>
        </div>
        <div class="pmenu-body">
          <div class="pmenu-sec">
            ${PMENU_PRIMARY_ITEMS.map((item) => renderPrimaryPmenuItem(item, currentPath, this.#homeBadgeCount)).join("")}
            <button
              class="pmenu-item pmenu-more-btn"
              data-pmenu-more
              type="button"
              title="Stages, system &amp; developer tools"
              aria-expanded="${this.#pmoreOpen ? "true" : "false"}"
              aria-controls="pmMore"
            >
              <span class="pmenu-ic"><svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><circle cx="8.5" cy="8.5" r="5.5"/><path d="M12.7 12.7 L17.5 17.5"/></svg></span>
              <span class="pmenu-lb">Stages + more</span>
              <span class="pmenu-acc-car"><svg viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M3.5 2 L6.5 5 L3.5 8"/></svg></span>
            </button>
          </div>
        </div>
        <div class="pmenu-bottom">
          <div class="pmenu-id" data-path="${PROFILE_PATH}" role="button" tabindex="0" title="Profile">
            <span class="pmenu-av">${initials}</span>
            <span class="pmenu-id-tx"><span class="pmenu-id-name">${escapeHtml(deviceLabel)}</span></span>
          </div>
        </div>
      </nav>
      ${renderPmoreMarkup({ currentPath, items: this.#items, open: this.#pmoreOpen, recentPaths: this.#recents })}
    `;
        if (searchSnapshot) {
            this.#restoreSearchSnapshot(searchSnapshot);
        }
        this.#pmoreWasOpen = this.#pmoreOpen;
    }
    #handleClick = (event) => {
        const target = event.target;
        // `Element`, not `HTMLElement`: every `.pmenu-item`/`.pmenu-collapse` button renders an
        // `<svg>` icon child (`.pmenu-ic > svg`), so a real mouse click frequently lands on that
        // SVG/path (an `SVGElement`, not an `HTMLElement`) rather than the button itself. Gating
        // on `HTMLElement` silently swallowed those clicks — `.closest()` is defined on `Element`
        // and works identically for both, so there is no need for the narrower type here.
        if (!(target instanceof Element)) {
            return;
        }
        if (target.closest("[data-pmenu-collapse]")) {
            this.#collapsed = !this.#collapsed;
            this.render();
            return;
        }
        if (target.closest("[data-pmenu-more]")) {
            if (this.#justOpenedByHover && this.#pmoreOpen) {
                // This click is the tail end of the SAME gesture whose `mouseover` just opened
                // the panel (see `#justOpenedByHover`'s own doc) — consume the flag and leave it
                // open rather than toggling it back closed. The hover-open itself deliberately
                // moved no focus (see the class doc's "Hover-open" section), and this `click`
                // handler always runs AFTER `mousedown`'s native default action (which, in
                // Chromium, focuses the `<button>` itself) — so granting `.pmore-search` the
                // click-open autofocus here is both race-free and the first focus move of the
                // whole gesture.
                this.#justOpenedByHover = false;
                this.querySelector(".pmore-search")?.focus();
            }
            else if (this.#pmoreOpen) {
                this.#closePmore();
            }
            else {
                this.#openPmore();
            }
            return;
        }
        const item = target.closest("[data-path]");
        if (item) {
            const path = item.dataset.path;
            // Recents (F5): a path present in `#items` is a registry-installed stage reached
            // through the flyout (either a `.pmore-sec` entry or a `.pm-recent` chip echoing one)
            // — push/re-front it. Primary Home/Agents/Friends rows and the profile chip are never
            // in `#items`, so they never pollute recents (mirrors the source's own
            // `RECENT_VIEWS`-gated `pushRecent`).
            if (path !== undefined && this.#items.some((navItem) => navItem.path === path)) {
                this.#pushRecent(path);
            }
            if (this.#pmoreOpen && target.closest(".pmore")) {
                // Selecting an entry (or a recents chip) also navigates (below) — once the target
                // stage mounts, app-shell.ts's own `#renderPath` already moves focus into
                // `[data-stage-outlet]` (`#focusOutlet()`, its established convention for every
                // navigation, not just this one). Restoring focus to the trigger here would just
                // get silently overridden a moment later, so skip it and let that existing, correct
                // focus management own the outcome — the flyout still closes (`aria-hidden` +
                // `inert`) synchronously either way.
                this.#closePmore({ restoreFocus: false });
            }
            this.dispatchEvent(new CustomEvent("navigate", {
                bubbles: true,
                composed: true,
                detail: { path: item.dataset.path },
            }));
        }
    };
    #handleInput = (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement) || !target.matches(".pmore-search")) {
            return;
        }
        filterPmoreEntries(this, target.value);
    };
    // Escape-dismiss for the open flyout — document-level, active only while open, exactly
    // like `#handleOutsideClick` and a verbatim port of the source's own document `keydown`
    // Escape close (`core/shell/menu/index.js` ~567-569). See the class doc: handling Escape
    // only via this component's bubbling `keydown` listener made it a dead key whenever focus
    // sat outside `<node-shell-pmenu>` (issue #1669).
    #handleDocumentKeyDown = (event) => {
        if (event.key === "Escape" && this.#pmoreOpen) {
            this.#closePmore();
        }
    };
    #handleKeyDown = (event) => {
        if (!(event instanceof KeyboardEvent)) {
            return;
        }
        // Scoped to `.pmenu .pmenu-item[data-path]` (the primary Home/Agents/Friends entries,
        // inside the `<nav class="pmenu">` element itself) — deliberately excludes `.pmenu-id`
        // (the profile chip, a separate identity control) AND the `.pmore` flyout's own
        // `[data-pmore-entry]` buttons, which duplicate the same `.pmenu-item[data-path]`
        // shape/class but live in a sibling panel outside `.pmenu`, not this roving sequence.
        // (Escape-dismiss for the flyout lives in `#handleDocumentKeyDown`, not here.)
        const buttons = Array.from(this.querySelectorAll(".pmenu .pmenu-item[data-path]"));
        const target = event.target instanceof Element
            ? event.target.closest(".pmenu .pmenu-item[data-path]")
            : null;
        const currentIndex = buttons.findIndex((button) => button === target);
        if (currentIndex === -1) {
            return;
        }
        let nextIndex = currentIndex;
        switch (event.key) {
            case "ArrowDown":
            case "ArrowRight":
                nextIndex = Math.min(buttons.length - 1, currentIndex + 1);
                break;
            case "ArrowUp":
            case "ArrowLeft":
                nextIndex = Math.max(0, currentIndex - 1);
                break;
            case "Home":
                nextIndex = 0;
                break;
            case "End":
                nextIndex = buttons.length - 1;
                break;
            default:
                return;
        }
        event.preventDefault();
        buttons[nextIndex]?.focus();
    };
    // Closes the flyout on a click that lands outside both `.pmore` and its trigger
    // (`.pmenu-more-btn`) — port of the source flyout's own `document.addEventListener`
    // click-outside behavior (`core/shell/menu/index.js`). Checked via `event.composedPath()`
    // rather than `event.target.closest(...)`: `.pmore`/`.pmenu-more-btn` live inside
    // app-shell's open shadow root, and a `click` listener registered on `document` (outside
    // that shadow root) observes a retargeted `event.target` — always the shadow host, never
    // the actual inner element — so `.closest()` on `event.target` can't tell an inside click
    // from an outside one. `composedPath()` returns the real, un-retargeted path.
    #handleOutsideClick = (event) => {
        const insideFlyout = event.composedPath().some((node) => (node instanceof Element && (node.classList.contains("pmore") || node.classList.contains("pmenu-more-btn"))));
        if (insideFlyout) {
            return;
        }
        this.#closePmore();
    };
    // Hover-open (F5) — delegated `mouseover`/`mouseout` on `this` rather than non-bubbling
    // `mouseenter`/`mouseleave` directly on `.pmenu-more-btn`/`.pmore`: `render()` destructively
    // rebuilds those nodes on every prop change, which would silently drop direct listeners.
    // `relatedTarget` (the element the pointer came from/is going to) distinguishes a genuine
    // region entry/exit from movement WITHIN the trigger+panel region (e.g. crossing from the
    // button into the panel) the same way the source's own `mouseenter`/`mouseleave` pair does
    // — with one addition the source doesn't need: `#isSynthesizedHoverRefire` first discards
    // the browser-synthesized boundary events that follow every destructive rebuild under a
    // stationary cursor, and a DETACHED `relatedTarget` (Chromium hands the replaced old node
    // to the next genuine event after a rebuild) counts as "outside" — matching only against
    // still-connected nodes — so a genuine entry is never mistaken for internal movement.
    // See the class doc's "Hover-open" section (issue #1669).
    #handleMouseOver = (event) => {
        if (!this.#finePointer || !(event instanceof MouseEvent) || !(event.target instanceof Element)) {
            return;
        }
        if (this.#isSynthesizedHoverRefire(event)) {
            return;
        }
        const target = event.target;
        if (!target.closest(".pmenu-more-btn, .pmore")) {
            return;
        }
        const related = event.relatedTarget;
        const cameFromInside = related instanceof Element && related.isConnected
            && related.closest(".pmenu-more-btn, .pmore") !== null;
        this.#cancelHoverClose();
        if (!cameFromInside && target.closest(".pmenu-more-btn")) {
            const wasOpen = this.#pmoreOpen;
            this.#openPmore({ focusSearch: false });
            if (!wasOpen) {
                this.#justOpenedByHover = true;
            }
        }
    };
    #handleMouseOut = (event) => {
        if (!this.#finePointer || !(event instanceof MouseEvent) || !(event.target instanceof Element)) {
            return;
        }
        if (this.#isSynthesizedHoverRefire(event)) {
            return;
        }
        const target = event.target;
        if (!target.closest(".pmenu-more-btn, .pmore")) {
            return;
        }
        const related = event.relatedTarget;
        const stillInside = related instanceof Element && related.isConnected
            && related.closest(".pmenu-more-btn, .pmore") !== null;
        if (stillInside) {
            return;
        }
        this.#scheduleHoverClose();
    };
    /** Tracks the real pointer position — the reference point `#isSynthesizedHoverRefire`
     * compares boundary events against. Only `mousemove` updates it, and browsers dispatch
     * the boundary events of a hit-target change BEFORE the `mousemove` carrying the same
     * new position (verified empirically on both Chromium and Firefox against this exact
     * shell), so a GENUINE `mouseover`/`mouseout` always observes a stale (different)
     * last-move position here, while a synthesized refire — dispatched with no pointer
     * movement at all — observes an exactly-equal one. */
    #handleMouseMove = (event) => {
        if (!(event instanceof MouseEvent)) {
            return;
        }
        this.#lastPointerX = event.clientX;
        this.#lastPointerY = event.clientY;
    };
    /** Whether a `mouseover`/`mouseout` is a browser-SYNTHESIZED refire against a rebuilt
     * DOM rather than genuine pointer movement — see the class doc's "Hover-open" section
     * (issue #1669). A refire is dispatched because `render()` replaced the node under a
     * STATIONARY cursor, so its coordinates always equal the last `mousemove`'s
     * (`#handleMouseMove`, above); a genuine boundary event is always dispatched BEFORE the
     * `mousemove` that carries its own new position, so it never matches. `relatedTarget`
     * is deliberately NOT part of this test: Gecko refires with `null`, Chromium usually
     * with the detached old node, but Gecko's post-close refire hands over a live attached
     * ancestor — coordinates are the only invariant all engines share. */
    #isSynthesizedHoverRefire(event) {
        return event.clientX === this.#lastPointerX && event.clientY === this.#lastPointerY;
    }
    #cancelHoverClose() {
        if (this.#hoverCloseTimer !== undefined) {
            clearTimeout(this.#hoverCloseTimer);
            this.#hoverCloseTimer = undefined;
        }
    }
    #scheduleHoverClose() {
        this.#cancelHoverClose();
        this.#hoverCloseTimer = setTimeout(() => {
            this.#hoverCloseTimer = undefined;
            // A hover-away close only restores focus when the flyout region actually OWNS focus
            // (the user clicked/tabbed into the panel after the hover-open, so closing would
            // otherwise strand focus inside the now-`inert` subtree). A pure hover fly-by never
            // moved focus in the first place — yanking it onto the trigger from wherever the
            // user was working was issue #1669's silent focus steal.
            const active = this.#activeElement();
            const focusInFlyout = active instanceof Element
                && active.closest(".pmore, .pmenu-more-btn") !== null;
            this.#closePmore({ restoreFocus: focusInFlyout });
        }, HOVER_CLOSE_DELAY);
    }
    #pushRecent(path) {
        this.#recents = [path, ...this.#recents.filter((existing) => existing !== path)].slice(0, RECENTS_LIMIT);
    }
    #openPmore(options = {}) {
        if (this.#pmoreOpen) {
            return;
        }
        const active = this.#activeElement();
        this.#previousFocus = active instanceof HTMLElement ? active : null;
        this.#pmoreOpen = true;
        this.render();
        document.addEventListener("click", this.#handleOutsideClick);
        document.addEventListener("keydown", this.#handleDocumentKeyDown);
        // Click-toggle opens autofocus the search input; hover-opens deliberately don't (see
        // the class doc's "Hover-open" section). Deferred to a microtask so the focus lands
        // after the same-task DOM work settles — the same convention `#closePmore` uses.
        if (options.focusSearch ?? true) {
            queueMicrotask(() => {
                if (this.isConnected && this.#pmoreOpen) {
                    this.querySelector(".pmore-search")?.focus();
                }
            });
        }
    }
    #closePmore(options = {}) {
        if (!this.#pmoreOpen) {
            return;
        }
        this.#cancelHoverClose();
        this.#justOpenedByHover = false;
        const restoreFocus = options.restoreFocus ?? true;
        this.#pmoreOpen = false;
        this.render();
        document.removeEventListener("click", this.#handleOutsideClick);
        document.removeEventListener("keydown", this.#handleDocumentKeyDown);
        const fallbackFocus = this.#previousFocus;
        this.#previousFocus = null;
        if (!restoreFocus) {
            return;
        }
        // Deferred (not focused synchronously above): a click on, say, `.pmenu-collapse` while
        // the flyout is open closes it via `#handleOutsideClick` (below) but ALSO runs its own
        // `render()` for the collapse toggle in the same click — queuing lets whichever
        // same-click renders happen first settle before re-resolving the trigger by selector
        // (see the class doc — a captured node reference wouldn't survive any of them anyway).
        queueMicrotask(() => {
            if (!this.isConnected) {
                return;
            }
            const target = this.querySelector(".pmenu-more-btn") ?? fallbackFocus;
            target?.focus();
        });
    }
    #activeElement() {
        const root = this.getRootNode();
        return root instanceof ShadowRoot ? root.activeElement : this.ownerDocument.activeElement;
    }
    #captureSearchSnapshot() {
        const input = this.querySelector(".pmore-search");
        if (!input) {
            return null;
        }
        return {
            value: input.value,
            selectionStart: input.selectionStart,
            selectionEnd: input.selectionEnd,
            focused: this.#activeElement() === input,
        };
    }
    #restoreSearchSnapshot(snapshot) {
        const input = this.querySelector(".pmore-search");
        if (!input) {
            return;
        }
        input.value = snapshot.value;
        filterPmoreEntries(this, snapshot.value);
        if (snapshot.focused) {
            input.focus();
            if (snapshot.selectionStart !== null && snapshot.selectionEnd !== null) {
                input.setSelectionRange(snapshot.selectionStart, snapshot.selectionEnd);
            }
        }
    }
}
function renderPrimaryPmenuItem(item, currentPath, homeBadgeCount) {
    const current = item.path === currentPath;
    const badge = item.name === "home" && homeBadgeCount !== undefined
        ? `<span class="pmenu-badge">${homeBadgeCount}</span>`
        : "";
    return `
    <button
      class="pmenu-item${current ? " active" : ""}"
      data-path="${item.path}"
      aria-current="${current ? "page" : "false"}"
      ${current ? "" : 'tabindex="-1"'}
      title="${escapeHtml(item.label)}"
      type="button"
    >
      <span class="pmenu-ic"><svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5">${item.icon}</svg></span>
      <span class="pmenu-lb">${escapeHtml(item.label)}</span>
      ${badge}
    </button>
  `;
}
function deviceInitials(label) {
    const initials = label
        .split(/\s+/u)
        .filter((word) => word.length > 0)
        .slice(0, 2)
        .map((word) => word[0].toUpperCase())
        .join("");
    return initials || "N";
}
//# sourceMappingURL=pmenu.js.map